/**
 * Axios instance — Baobab API client
 *
 * Every request automatically injects the current Supabase access token
 * as a Bearer header. The Supabase session IS the backend auth token
 * (baobab-api validates it via supabase.auth.getUser()).
 *
 * 401 responses redirect to /login and clear local state.
 * All error shapes are normalised to ApiError.
 */

import axios from 'axios'
import type { AxiosError } from 'axios'
import { supabase } from '@/lib/supabase'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ApiError {
  code: string
  message: string
  details?: unknown
  status?: number
}

export interface ApiResponse<T> {
  data: T
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    total_pages: number
  }
}

// ─── Instance ─────────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL
if (!API_BASE) {
  throw new Error(
    'VITE_API_URL is not set. ' +
    'Copy .env.example to .env.local and fill in the value.'
  )
}

const client = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ─── Request interceptor — inject Bearer token ────────────────────────────────

client.interceptors.request.use(async (config) => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`
    }
  } catch {
    // If Supabase is unavailable, send request without token;
    // the server will return 401 and the response interceptor will handle it.
  }

  return config
})

// ─── Response interceptor — normalise errors ──────────────────────────────────

client.interceptors.response.use(
  (response) => response,

  async (error: AxiosError<{ error?: { code?: string; message?: string; details?: unknown } }>) => {
    const status = error.response?.status

    // 401 → clear session and redirect to login
    if (status === 401) {
      await supabase.auth.signOut()
      window.location.href = '/login'
      return Promise.reject(normaliseError(error))
    }

    return Promise.reject(normaliseError(error))
  }
)

function normaliseError(error: AxiosError<{ error?: { code?: string; message?: string; details?: unknown } }>): ApiError {
  if (error.response?.data?.error) {
    const serverError = error.response.data.error
    return {
      code: serverError.code ?? 'SERVER_ERROR',
      message: serverError.message ?? 'An unexpected error occurred',
      details: serverError,
      status: error.response.status,
    }
  }

  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return {
      code: 'REQUEST_TIMEOUT',
      message: 'The request timed out. Please try again.',
      status: 408,
    }
  }

  if (!error.response) {
    return {
      code: 'NETWORK_ERROR',
      message: 'Unable to reach the server. Check your connection.',
    }
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: error.message ?? 'An unexpected error occurred',
    status: error.response?.status,
  }
}

export default client
